import { useDroppable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { memo, useMemo } from "react";

import { AdminGetRoutesLocationsResDto } from "service/api/estabrookSchemas";
import { makeSxStyles } from "ui";
import DraggableOrder from "./draggable-order";
import { ClickedAddress } from "../../../../shared/hooks/use-address-click-handler";
import useRoutePlannerStore from "../../../../state/route-planner-store";
import useTimetableZoomStore from "../../../../state/timetable-zoom-store";
import { RouteStatus } from "../helpers/route-status.enum";
import { TimeAdjustments } from "../../../../hooks/use-realtime-time-adjustments";
import { computeCompletedStackShifts } from "../helpers/completed-stack-shifts";

export interface DropZoneDetails {
  id: string;
  items: AdminGetRoutesLocationsResDto[];
  routeId: string;
  icon?: React.ReactNode;
  color: string;
}

export interface DropZoneProps {
  dropZoneDetails: DropZoneDetails;
  onClickedAddress: (value: ClickedAddress) => void;
  handleMouseEnter: (orderId: string, event: React.MouseEvent) => void;
  handleMouseLeave: () => void;
  currentRouteStatus?: RouteStatus;
  isLastItem: boolean;
  travelTimeFromStartingPoint?: number; // Travel time from starting point to first location
  // New props for line hover functionality
  onLineHover?: (
    sourceLocationId: string,
    destinationLocationId: string,
  ) => void;
  onLineHoverEnd?: () => void;
  hoveredLineSource?: string;
  hoveredLineDestination?: string;
  nextLocationMap?: Record<string, string | undefined>; // Map of locationId -> nextLocationId
  nextLocationDataMap?: Record<
    string,
    AdminGetRoutesLocationsResDto | undefined
  >; // Map of locationId -> nextLocation object
  actualServiceTimeMap?: Record<string, number | undefined>; // Map of locationId -> actual service time (for live phase)
  adjustments?: Record<string, TimeAdjustments>; // Map of locationId -> time adjustments
  isUnassignedRoute?: boolean;
  firstLocationToRender?: string | null; // ID of the first location that will be rendered across all dropzones
  isDraggingGlobal?: boolean; // Track if any item is being dragged globally
  completedStackShifts?: Record<string, number>;
}

const DropZone: React.FC<DropZoneProps> = ({
  dropZoneDetails,
  handleMouseEnter,
  handleMouseLeave,
  onClickedAddress,
  currentRouteStatus,
  isLastItem,
  travelTimeFromStartingPoint,
  onLineHover,
  onLineHoverEnd,
  hoveredLineSource,
  hoveredLineDestination,
  nextLocationMap,
  nextLocationDataMap,
  actualServiceTimeMap,
  adjustments,
  isUnassignedRoute = false,
  firstLocationToRender,
  isDraggingGlobal = false,
  completedStackShifts,
}) => {
  const { id, items, routeId, icon, color } = dropZoneDetails;
  // Use selector to only subscribe to selectedAddresses, not the entire store
  const selectedAddresses = useRoutePlannerStore(
    (state) => state.selectedAddresses,
  );

  // Create a Set of selected IDs for O(1) lookups instead of O(n) array lookups
  const selectedIdsSet = useMemo(
    () => new Set(selectedAddresses.map((addr) => addr.id)),
    [selectedAddresses],
  );

  const { zoomLevel } = useTimetableZoomStore();

  // Leftward offsets that fan out runs of overlapping completed
  // zero-service-time boxes (deck-of-cards stagger)
  const stackShifts = useMemo(
    () =>
      computeCompletedStackShifts(items, {
        adjustments,
        actualServiceTimeMap,
        zoomLevel,
        currentRouteStatus,
      }),
    [items, adjustments, actualServiceTimeMap, zoomLevel, currentRouteStatus],
  );

  // Extract shift type from the drop zone id for better data handling
  const shiftType = id.includes("-")
    ? id.substring(id.lastIndexOf("-") + 1)
    : "";

  const isSkippedZone = shiftType === "skipped";

  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      routeId: routeId,
      shiftType: shiftType,
      itemCount: items?.length || 0,
    },
  });
  const styles = useStyles();

  // For skipped zone, show hover state if dragging OR if actually over
  const shouldShowSkippedHover = isSkippedZone && (isDraggingGlobal || isOver);

  // For skipped zone, we need a wrapper structure to ensure the entire area is droppable
  if (isSkippedZone) {
    return (
      <Box
        ref={setNodeRef}
        sx={{
          ...styles.dropZone,
          // Was minWidth: "100%" (meant to make the whole reserved column
          // area droppable, not just the chips) — but inside a
          // table-layout:auto column, "100% of my container" is circular
          // (the column's own width is derived FROM this element's content
          // width), which was quietly inflating the "skipped" column
          // regardless of its actual chip content. A fixed floor gives a
          // reasonably forgiving drop target without the circular
          // reference; customCollisionDetection in route-planner-table.tsx
          // also already adds a buffer around skipped zones for easier
          // targeting, so this isn't the only thing carrying that UX.
          minWidth: "100px",
          minHeight: "20px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          position: "relative",
          // Above normal sibling content within the row (for the drag-hover
          // highlight below), but well below the table's sticky header
          // (zIndex: 10 in route-planner-table.tsx) — this used to also be
          // 10, which let it escape a non-stacking-context ancestor cell and
          // paint over the sticky header instead of under it.
          zIndex: 2,
          pointerEvents: "auto", // Ensure pointer events are captured
          // Show visual feedback when dragging
          ...(shouldShowSkippedHover && {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            border: "2px dashed rgba(255, 255, 255, 0.4)",
            borderRadius: "4px",
            boxShadow: "0 0 8px rgba(255, 255, 255, 0.2)", // Add subtle glow
          }),
        }}
        data-over={isOver || shouldShowSkippedHover}
      >
        <Box sx={styles.itemsContainer}>
          {items.map((item, index) => {
            const nextLocationId = nextLocationMap
              ? nextLocationMap[item.locationId]
              : index < items.length - 1
                ? items[index + 1].locationId
                : undefined;

            const nextLocation = nextLocationDataMap
              ? nextLocationDataMap[item.locationId]
              : index < items.length - 1
                ? items[index + 1]
                : undefined;

            return (
              <DraggableOrder
                key={item.locationId}
                id={item.locationId}
                locationSequence={item.locationSequence || 0}
                order={item}
                routeId={routeId}
                color={color}
                selected={selectedIdsSet.has(item.locationId)}
                onClickedAddress={onClickedAddress}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                currentRouteStatus={currentRouteStatus}
                isLastItem={index === items.length - 1 && isLastItem}
                travelTimeFromStartingPoint={travelTimeFromStartingPoint}
                onLineHover={onLineHover}
                onLineHoverEnd={onLineHoverEnd}
                hoveredLineSource={hoveredLineSource}
                hoveredLineDestination={hoveredLineDestination}
                nextLocationId={nextLocationId}
                nextLocation={nextLocation}
                actualServiceTime={actualServiceTimeMap?.[item.locationId]}
                adjustedServiceTime={
                  adjustments?.[item.locationId]?.adjustedServiceTime
                }
                adjustedTravelTime={
                  adjustments?.[item.locationId]?.adjustedTravelTime
                }
                isUnassignedRoute={isUnassignedRoute}
                isFirstLocationToRender={
                  item.locationId === firstLocationToRender
                }
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  // For non-skipped zones, use the original structure
  return (
    <Box
      sx={{
        ...styles.dropZone,
        ...(currentRouteStatus === RouteStatus.Planning
          ? {
              borderRight: "1px solid #C5C6C9",
              marginRight: "16px",
              minWidth: "10vw",
            }
          : {
              minWidth: !items.length
                ? "30px"
                : id.includes("delayed") &&
                    currentRouteStatus !== RouteStatus.Live
                  ? "100%"
                  : "unset",
            }),
      }}
      data-over={isOver}
    >
      {(currentRouteStatus === RouteStatus.Planning ||
        id.includes("delayed")) &&
      icon &&
      !isUnassignedRoute ? (
        currentRouteStatus === RouteStatus.Live ? (
          <Box sx={{ position: "absolute", left: -20 }}>{icon}</Box>
        ) : (
          icon
        )
      ) : null}
      <Box
        ref={setNodeRef}
        sx={{
          ...styles.itemsContainer,
          ...(currentRouteStatus === RouteStatus.Live &&
            items.length > 0 && {
              minWidth: 0,
            }),
        }}
      >
        {items.map((item, index) => {
          // Use the cross-zone nextLocationMap if available, otherwise fallback to within-zone logic
          const nextLocationId = nextLocationMap
            ? nextLocationMap[item.locationId]
            : index < items.length - 1
              ? items[index + 1].locationId
              : undefined;

          // Get the next location object for travel time data
          const nextLocation = nextLocationDataMap
            ? nextLocationDataMap[item.locationId]
            : index < items.length - 1
              ? items[index + 1]
              : undefined;

          return (
            <DraggableOrder
              key={item.locationId}
              id={item.locationId}
              locationSequence={item.locationSequence || 0}
              order={item}
              routeId={routeId}
              color={color}
              selected={selectedIdsSet.has(item.locationId)}
              onClickedAddress={onClickedAddress}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
              currentRouteStatus={currentRouteStatus}
              isLastItem={index === items.length - 1 && isLastItem}
              travelTimeFromStartingPoint={travelTimeFromStartingPoint}
              onLineHover={onLineHover}
              onLineHoverEnd={onLineHoverEnd}
              hoveredLineSource={hoveredLineSource}
              hoveredLineDestination={hoveredLineDestination}
              nextLocationId={nextLocationId}
              nextLocation={nextLocation}
              actualServiceTime={actualServiceTimeMap?.[item.locationId]}
              adjustedServiceTime={
                adjustments?.[item.locationId]?.adjustedServiceTime
              }
              adjustedTravelTime={
                adjustments?.[item.locationId]?.adjustedTravelTime
              }
              isUnassignedRoute={isUnassignedRoute}
              isFirstLocationToRender={
                item.locationId === firstLocationToRender
              }
              stackShift={
                (completedStackShifts ?? stackShifts)[item.locationId]
              }
            />
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles((theme) => ({
  dropZone: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "20px",
    padding: "0",
    width: "100%",
    minWidth: "100px",
    position: "relative",
    "&:last-of-type": {
      borderRight: "none",
      marginRight: "0px",
    },
    "> svg": {
      marginRight: "8px",
    },
    "&[data-over='true']": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "30px",
    height: "20px",
    borderRadius: "4px",
    flexWrap: "nowrap", // Ensure items stay in a single row
    // Removed transitions that might interfere with drag
    // "&:hover": {
    //   backgroundColor: theme.palette.action.hover,
    // },
    // "& > div": {
    //   transition: "transform 0.2s ease",
    // },
  },
}));

export default memo(DropZone);
